/**
 * Adds and removes the free gift line as the cart crosses the threshold in
 * theme settings. Pricing is not our job — a native Buy X get Y automatic
 * discount in the admin zeroes the line; Shopify just never adds it for us.
 *
 * The gift is tagged with a `_free_gift` line item property (leading
 * underscore hides it from customer-facing templates). The tag, not the price,
 * identifies the gift — and it forces a separate cart line, so a customer who
 * also buys the gift product at full price keeps two rows instead of Shopify
 * merging them into one line of quantity 2.
 *
 * @extends HTMLElement
 */

const GIFT_PROPERTY = '_free_gift';
const MAX_ADD_ATTEMPTS = 3;

export class CartGift extends HTMLElement {
  constructor() {
    super();
    this.busy = false;
    this.addAttempts = 0;
    this.giftRejected = false;
    this.onCartChange = this.onCartChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
    // cart.js runs earlier in the document and seeds state from #cart-data, so
    // a cart that already qualifies on page load is handled here.
    this.sync(window.Cart?.state);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
  }

  get variantId() {
    return Number(this.dataset.variantId);
  }

  /** @returns {number} Threshold in the currency's subunit. */
  get threshold() {
    return Number(this.dataset.threshold);
  }

  onCartChange(event) {
    this.sync(event.detail.cart);
  }

  /**
   * @param {object|null} cart Cart state from cart.js.
   */
  async sync(cart) {
    // `busy` covers re-entry: window.Cart dispatches cart:change before its
    // promise resolves, so our own mutations call straight back into here.
    if (!cart || this.busy || !this.variantId || !this.threshold) { return; }

    const items = cart.items || [];
    const giftLine = items.find((item) => item.properties?.[GIFT_PROPERTY]);

    // Mirrors how Shopify scores the discount: the gift product sits outside
    // the eligible collection, so its own price must not count toward the
    // threshold that unlocks it.
    let eligibleSubtotal = 0;
    for (const item of items) {
      if (!item.properties?.[GIFT_PROPERTY]) { eligibleSubtotal += item.final_line_price; }
    }

    const qualifies = (eligibleSubtotal >= this.threshold);

    /* The customer already bought the gift product themselves. The discount
       grants one unit per order, so it has already zeroed their line — adding a
       tagged duplicate would come back charged and be rejected below. Only
       reachable when the gift is a product customers can also buy; a dedicated
       gift product never hits this. */
    const alreadyInCart = items.some(
      (item) => !item.properties?.[GIFT_PROPERTY] && item.variant_id === this.variantId,
    );

    // A cart that drops back under the threshold is a fresh attempt.
    if (!qualifies) {
      this.giftRejected = false;
      this.addAttempts = 0;
    }

    this.busy = true;
    try {
      if (!giftLine) {
        if (qualifies && !alreadyInCart && !this.giftRejected && this.addAttempts < MAX_ADD_ATTEMPTS) {
          this.addAttempts += 1;
          await this.addGift();
          await this.rejectIfCharged();
        }
      } else if (!qualifies) {
        await this.setGiftQuantity(giftLine, 0);
      } else if (giftLine.quantity > 1) {
        // The discount only zeroes one unit; the rest would be charged.
        await this.setGiftQuantity(giftLine, 1);
      } else if (giftLine.final_line_price > 0) {
        await this.reject(giftLine);
      }
    } catch {
      // A failed gift must never block checkout; cart.js already dispatched
      // cart:error.
    } finally {
      this.busy = false;
    }
  }

  /**
   * Safety net. If the admin discount did not zero the line we just added —
   * theme threshold lower than the discount's minimum, discount inactive,
   * expired, or scoped to other customers — pull it rather than charge for a
   * "free" gift. Checked immediately after the add, while state is fresh.
   */
  rejectIfCharged() {
    const added = window.Cart.state?.items?.find((item) => item.properties?.[GIFT_PROPERTY]);
    if (!added || added.final_line_price === 0) { return Promise.resolve(); }
    return this.reject(added);
  }

  /** Removes the gift and stops retrying, so this can't oscillate. */
  reject(giftLine) {
    this.giftRejected = true;
    return this.setGiftQuantity(giftLine, 0);
  }

  addGift() {
    const body = new FormData();
    body.append('id', String(this.variantId));
    body.append('quantity', '1');
    body.append(`properties[${GIFT_PROPERTY}]`, 'true');
    return window.Cart.add(body, this);
  }

  /**
   * @param {object} giftLine Gift line item from cart state.
   * @param {number} quantity 0 removes the line.
   */
  setGiftQuantity(giftLine, quantity) {
    // Keyed by line, not variant: the same variant may also sit in the cart as
    // a normal paid line, which must not be touched.
    return window.Cart.change({id: giftLine.key, quantity}, this);
  }
}

if (!customElements.get('cart-gift')) {
  customElements.define('cart-gift', CartGift);
}
